// pages/Simulate/workingCopyStorage.ts — Moving Out arc, drop 2 wiring
// ─────────────────────────────────────────────────────────────────────────
// WORKING-COPY RECOVERY. The plan you were editing when the tab closed.
//
// Contract 4.4: three key families, a 30-day TTL checked on read, a conflict
// notice when the saved plan moved underneath the draft, and registration in
// localExport so the local export does not go quietly incomplete.
//
// THREE FAMILIES, and the split is not arbitrary.
//
//   nisk.wc.plan.<planId>     a draft against a SAVED plan. Keyed by plan
//                             id, because two plans can each have a draft
//                             and one must not overwrite the other.
//   nisk.wc.new               the draft with NO plan behind it yet. There
//                             is exactly one, because there is no id to key
//                             it by until the first save mints one.
//   nisk.wc.anon.<planId>     the anonymous namespace. Separate rather than
//                             mode-switched, so signing in can COMPARE the
//                             two rather than having already clobbered one.
//
// That last one is the reason the families are separate at all. Contract
// 4.4 requires asking explicitly when both namespaces hold recoveries, and
// you cannot ask about something a mode-aware single key has already
// overwritten.
//
// THE TTL IS CHECKED ON READ, NOT ON WRITE. A draft that expires while the
// tab is closed must not come back, and there is no write to hang the check
// on. Reading an expired record deletes it and returns null, so the expiry
// is self-healing rather than needing a sweep.
//
// THE CONFLICT RULE. `forkedFromUpdatedAt` records what the saved plan's
// timestamp was when the draft forked. If the saved plan has moved since,
// the draft was made against a plan that no longer exists in that form, and
// the person gets a choice rather than a silent restore. This is shown
// BEFORE the ordinary restore notice, because "your plan changed on another
// device" is a different question from "you have unsaved work".
//
// Pure and import-free (seams section 7). Storage is injected, so the tests
// do not need jsdom and a caller can pass sessionStorage, a mock, or
// nothing at all.
//
// Copy rule: no em or en dashes in any user-facing string.
// ─────────────────────────────────────────────────────────────────────────

/** 30 days. Contract 4.4. Long enough that a holiday does not lose work,
 *  short enough that a year-old draft does not resurface as a surprise. */
export const RECOVERY_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export const WC_PREFIX_PLAN = 'nisk.wc.plan.';
export const WC_KEY_NEW = 'nisk.wc.new';
export const WC_PREFIX_ANON = 'nisk.wc.anon.';

/** Every prefix a local export must walk. Registering these in
 *  localExport.ts is the difference between an export that is complete and
 *  one that quietly is not. */
export const WC_EXPORT_FAMILIES: ReadonlyArray<{
	label: string;
	prefix: string;
}> = [
	{ label: 'planWorkingCopies', prefix: WC_PREFIX_PLAN },
	{ label: 'planWorkingCopiesAnon', prefix: WC_PREFIX_ANON },
	{ label: 'planWorkingCopyNew', prefix: WC_KEY_NEW },
];

/** The minimal storage surface. Injected so tests need no DOM and a caller
 *  can pass sessionStorage without this module knowing the difference. */
export interface KeyValueStore {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
	removeItem(key: string): void;
	readonly length: number;
	key(index: number): string | null;
}

export interface WorkingCopyRecord {
	v: 1;
	/** Written at, epoch ms. The TTL is measured from here. */
	ts: number;
	/** The plan this drafts against, or null for an unsaved first plan. */
	planId: string | null;
	/** The saved plan's `_lastUpdated` when the draft forked from it. Null
	 *  when there was nothing to fork from. */
	forkedFromUpdatedAt: number | null;
	/** Opaque. LeverModel[] as authored. This module does not look inside:
	 *  it is a courier, and a courier that parses its cargo acquires a
	 *  dependency on the cargo's shape. */
	levers: unknown[];
	/** Working question instances, same treatment. */
	questionInstances?: unknown[];
	targetAge: number | null;
	/** For the notice, so it can say how much is at stake without the
	 *  caller re-running the diff before deciding whether to offer. */
	changeCount: number;
	planName: string;
}

export type Namespace = 'signed-in' | 'anonymous';

export function workingCopyKey(
	planId: string | null,
	ns: Namespace = 'signed-in',
): string {
	if (planId === null) return WC_KEY_NEW;
	return ns === 'anonymous'
		? `${WC_PREFIX_ANON}${planId}`
		: `${WC_PREFIX_PLAN}${planId}`;
}

// ─── Write ────────────────────────────────────────────────────────────────

export function writeWorkingCopy(
	store: KeyValueStore | null,
	record: WorkingCopyRecord,
	ns: Namespace = 'signed-in',
): boolean {
	if (store === null) return false;
	try {
		store.setItem(
			workingCopyKey(record.planId, ns),
			JSON.stringify(record),
		);
		return true;
	} catch {
		// Quota, or private browsing. Losing a draft is bad; taking the
		// page down because we could not save one is worse.
		return false;
	}
}

// ─── Read ─────────────────────────────────────────────────────────────────

export type ReadResult =
	| { kind: 'none' }
	| { kind: 'expired' }
	| { kind: 'corrupt' }
	| { kind: 'found'; record: WorkingCopyRecord };

/**
 * Read a working copy, expiring it in place if it is stale.
 *
 * Expiry and corruption both DELETE. A record that cannot be parsed will
 * never parse, and leaving it costs a failed parse on every read forever.
 */
export function readWorkingCopy(
	store: KeyValueStore | null,
	planId: string | null,
	now: number,
	ns: Namespace = 'signed-in',
): ReadResult {
	if (store === null) return { kind: 'none' };
	const key = workingCopyKey(planId, ns);

	let raw: string | null;
	try {
		raw = store.getItem(key);
	} catch {
		return { kind: 'none' };
	}
	if (raw === null) return { kind: 'none' };

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		safeRemove(store, key);
		return { kind: 'corrupt' };
	}

	if (!isRecord(parsed)) {
		safeRemove(store, key);
		return { kind: 'corrupt' };
	}

	const ts = typeof parsed.ts === 'number' ? parsed.ts : null;
	if (ts === null || !Array.isArray(parsed.levers)) {
		safeRemove(store, key);
		return { kind: 'corrupt' };
	}

	if (now - ts > RECOVERY_TTL_MS) {
		safeRemove(store, key);
		return { kind: 'expired' };
	}

	return { kind: 'found', record: parsed as unknown as WorkingCopyRecord };
}

export function clearWorkingCopy(
	store: KeyValueStore | null,
	planId: string | null,
	ns: Namespace = 'signed-in',
): void {
	if (store === null) return;
	safeRemove(store, workingCopyKey(planId, ns));
}

function safeRemove(store: KeyValueStore, key: string): void {
	try {
		store.removeItem(key);
	} catch {
		/* ignore */
	}
}

function isRecord(v: unknown): v is Record<string, unknown> {
	return typeof v === 'object' && v !== null && !Array.isArray(v);
}

// ─── Namespace scanning, for the sign-in question ─────────────────────────

/** Every working-copy key present in a namespace. Used to answer "are there
 *  recoveries in BOTH namespaces", which contract 4.4 says must be asked
 *  explicitly rather than resolved by a rule. */
export function listWorkingCopies(
	store: KeyValueStore | null,
	ns: Namespace,
): string[] {
	if (store === null) return [];
	const prefix = ns === 'anonymous' ? WC_PREFIX_ANON : WC_PREFIX_PLAN;
	const out: string[] = [];
	try {
		for (let i = 0; i < store.length; i++) {
			const k = store.key(i);
			if (k === null) continue;
			if (k.startsWith(prefix)) out.push(k);
			// The unsaved-plan key belongs to the signed-in namespace only
			// when there is no anonymous session; scanning it here would
			// make it appear in both.
			else if (ns === 'signed-in' && k === WC_KEY_NEW) out.push(k);
		}
	} catch {
		return [];
	}
	return out.sort();
}

export interface MigrationQuestion {
	/** Both namespaces hold work, so the person must choose. */
	needsChoice: boolean;
	anonCount: number;
	signedInCount: number;
}

/**
 * What to ask when an anonymous session signs in.
 *
 * Contract 4.4: copy before delete, idempotent, and ask explicitly when
 * both namespaces hold recoveries. The rule this encodes is that a silent
 * merge is only safe when exactly one side has anything to lose.
 *
 * Lives in `SessionModeBridge`, NOT in Simulate: people sign in from
 * Dashboard, and a migration that only fires on a Simulate visit orphans
 * the key past the routing decision.
 */
export function migrationQuestion(
	store: KeyValueStore | null,
): MigrationQuestion {
	const anon = listWorkingCopies(store, 'anonymous');
	const signedIn = listWorkingCopies(store, 'signed-in');
	return {
		needsChoice: anon.length > 0 && signedIn.length > 0,
		anonCount: anon.length,
		signedInCount: signedIn.length,
	};
}

export interface MigrationOutcome {
	/** Plan ids whose anonymous draft now lives in the signed-in namespace. */
	migrated: string[];
	/** Plan ids where BOTH namespaces held a draft for the same plan.
	 *  Nothing was touched: contract 4.4 wants this asked, not resolved by
	 *  a rule, and there is no surface to ask on yet. */
	contested: string[];
	/** Plan ids with an anonymous draft and no signed-in plan behind them.
	 *  LEFT IN PLACE, deliberately. See the note on the function. */
	orphaned: string[];
	/** A copy failed, so nothing on that key was deleted. */
	incomplete: boolean;
}

/**
 * Move anonymous working copies into the signed-in namespace on sign-in.
 *
 * THE GATE IS EXISTENCE. `nisk.wc.anon.<planId>` is keyed by plan id, and
 * copying it across is only coherent if a plan with that id exists in the
 * signed-in world. Nothing adopts anonymous PLANS today: `adoptAnonData` in
 * lib/sessionMode has no importer, and scenariosSlice reads
 * `activeScenariosKey()`, which flips to the signed-in key the instant
 * auth.currentUser is non-null. So an ungated copy mints drafts against
 * plans that cannot be opened.
 *
 * ORPHANS ARE NOT DELETED. Eager deletion would be destructive on an
 * ordering hazard we cannot rule out: if adoption is ever wired and lands
 * after this runs, the draft it belonged to is already gone. The 30-day TTL
 * already reaps abandoned drafts on read, and leaving them makes this
 * self-correcting instead: a draft that is orphaned on one resolve migrates
 * on the next one, once its plan exists.
 *
 * COPY BEFORE DELETE, and idempotent, per contract 4.4. Running it twice is
 * a no-op, which is what lets the caller run it on every signed-in resolve
 * rather than only on a mid-session flip.
 *
 * Callers pass `knownPlanIds`; this module does not read the scenarios key,
 * because a storage courier that learns the scenario shape stops being one.
 */
export function migrateAnonymousWorkingCopies(
	store: KeyValueStore | null,
	knownPlanIds: readonly string[],
	now: number,
): MigrationOutcome {
	const out: MigrationOutcome = {
		migrated: [],
		contested: [],
		orphaned: [],
		incomplete: false,
	};
	if (store === null) return out;

	const known = new Set(knownPlanIds);
	// Snapshot before mutating: the loop deletes, and `key(i)` walks a live
	// index.
	const anonKeys = listWorkingCopies(store, 'anonymous');

	for (const key of anonKeys) {
		const planId = key.slice(WC_PREFIX_ANON.length);
		if (planId === '') continue;

		// Reading applies the TTL, so an anonymous draft that expired while
		// the person was away is reaped here rather than migrated forward.
		const source = readWorkingCopy(store, planId, now, 'anonymous');
		if (source.kind !== 'found') continue;

		if (!known.has(planId)) {
			out.orphaned.push(planId);
			continue;
		}

		const existing = readWorkingCopy(store, planId, now, 'signed-in');
		if (existing.kind === 'found') {
			out.contested.push(planId);
			continue;
		}

		if (!writeWorkingCopy(store, source.record, 'signed-in')) {
			// Quota, or private browsing. The anonymous key stays: losing
			// the draft is worse than leaving it where it was.
			out.incomplete = true;
			continue;
		}
		clearWorkingCopy(store, planId, 'anonymous');
		out.migrated.push(planId);
	}

	return out;
}

// ─── Conflict ─────────────────────────────────────────────────────────────

export type RestoreDecision =
	| { kind: 'none' }
	/** The ordinary case: unsaved work, no conflict. */
	| { kind: 'restore'; record: WorkingCopyRecord }
	/** The saved plan moved since the draft forked. Ask first. */
	| {
			kind: 'conflict';
			record: WorkingCopyRecord;
			savedUpdatedAt: number;
	  };

/**
 * Decide what to offer on open.
 *
 * The conflict check runs FIRST and the ordinary restore notice never
 * appears alongside it. "Your plan changed on another device" is a
 * different question from "you have unsaved work", and stacking them makes
 * the person answer the wrong one.
 */
export function restoreDecision(
	result: ReadResult,
	savedUpdatedAt: number | null,
): RestoreDecision {
	if (result.kind !== 'found') return { kind: 'none' };
	const record = result.record;

	if (
		savedUpdatedAt !== null &&
		record.forkedFromUpdatedAt !== null &&
		savedUpdatedAt !== record.forkedFromUpdatedAt
	) {
		return { kind: 'conflict', record, savedUpdatedAt };
	}
	return { kind: 'restore', record };
}

// ─── Copy ─────────────────────────────────────────────────────────────────

export function restoreNoticeCopy(record: WorkingCopyRecord): string {
	const n = record.changeCount;
	return n === 1
		? 'Unsaved plan changes found. You had 1 change in progress.'
		: `Unsaved plan changes found. You had ${n} changes in progress.`;
}

/**
 * The conflict notice. Describes BOTH sides in freedom age and change
 * count, per contract 4.4, because "there is a conflict" is not a thing
 * anyone can decide on.
 *
 * Freedom ages are passed in rather than computed: this module is a
 * courier, and computing a freedom age here would mean importing the
 * engine into a storage module.
 */
export function conflictNoticeCopy(input: {
	changeCount: number;
	draftFreedomAge: number | null;
	savedFreedomAge: number | null;
}): string {
	const n = input.changeCount;
	const changes = n === 1 ? '1 unsaved change' : `${n} unsaved changes`;

	const draft =
		input.draftFreedomAge === null
			? 'does not reach freedom by age 100'
			: `reaches freedom at ${input.draftFreedomAge}`;
	const saved =
		input.savedFreedomAge === null
			? 'does not reach freedom by age 100'
			: `reaches freedom at ${input.savedFreedomAge}`;

	return `This plan changed somewhere else while you had ${changes} in progress. Your unsaved version ${draft}. The saved version ${saved}.`;
}

export const CONFLICT_KEEP_MINE = 'Keep my unsaved changes';
export const CONFLICT_KEEP_SAVED = 'Use the saved version';
export const RESTORE_LABEL = 'Restore them';
export const DISCARD_LABEL = 'Discard them';
